import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { StickyNote, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useCanAccess } from "../../hooks/useCanAccess";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import {
  fetchOverlayFeedback,
  getOverlayEnabled,
  getOverlayRecords,
  getOverlayLoading,
  setOverlayEnabled,
  openDetailModal,
} from "../../features/devFeedback/devFeedbackSlice";
import { DevFeedbackProps, DevFeedbackSeverity } from "../../types/devFeedback";
import { resolveFeedbackPosition, ResolvedFeedbackPosition } from "../../lib/feedbackOverlayPosition";
import { t } from "../../locales";
import DevFeedbackDetailModal from "./DevFeedbackDetailModal";

const severityDotClass: Record<DevFeedbackSeverity, string> = {
  bug: "bg-rose-500",
  ui: "bg-amber-500",
  suggestion: "bg-blue-500",
  other: "bg-gray-500",
};

const severityNoteClass: Record<DevFeedbackSeverity, string> = {
  bug: "border-rose-400 bg-rose-50",
  ui: "border-amber-400 bg-amber-50",
  suggestion: "border-blue-400 bg-blue-50",
  other: "border-gray-400 bg-gray-50",
};

type PositionedItem = { feedback: DevFeedbackProps; position: ResolvedFeedbackPosition };

/**
 * Renders open/in-progress dev-feedback for the CURRENT page directly on top
 * of the live UI as translucent sticky notes, toggled from a fixed
 * bottom-right button. Scoped strictly to `location.pathname` — every route
 * change clears and re-fetches, so notes never leak across pages.
 */
const FeedbackOverlayLayer: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const overlayEnabled = useAppSelector(getOverlayEnabled);
  const records = useAppSelector(getOverlayRecords);
  const loading = useAppSelector(getOverlayLoading);
  const labels = t.devFeedback;

  const [positions, setPositions] = useState<Record<number, ResolvedFeedbackPosition>>({});

  const permissionsReady = !!loggingUser?.permissions && !!selectedBranch;
  const canReview = permissionsReady && canAccess("dev-feedback");

  useEffect(() => {
    if (!canReview || !overlayEnabled) return;
    dispatch(fetchOverlayFeedback({ page_path: location.pathname }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, overlayEnabled, location.pathname]);

  const recomputePositions = useCallback(() => {
    const next: Record<number, ResolvedFeedbackPosition> = {};
    records.forEach((record) => {
      const pos = resolveFeedbackPosition(record);
      if (pos) next[record.id] = pos;
    });
    setPositions(next);
  }, [records]);

  useEffect(() => {
    recomputePositions();
  }, [recomputePositions]);

  useEffect(() => {
    if (!overlayEnabled) return;
    let raf = 0;
    const onResize = () => {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(recomputePositions);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.cancelAnimationFrame(raf);
    };
  }, [overlayEnabled, recomputePositions]);

  const items: PositionedItem[] = useMemo(
    () =>
      records
        .filter((r) => positions[r.id])
        .map((feedback) => ({ feedback, position: positions[feedback.id] })),
    [records, positions]
  );

  if (!canReview) return null;

  const toggle = () => dispatch(setOverlayEnabled(!overlayEnabled));

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        title={overlayEnabled ? labels.overlayHideButton : labels.overlayShowButton}
        aria-label={overlayEnabled ? labels.overlayHideButton : labels.overlayShowButton}
        className={`fixed bottom-6 right-6 z-[60] flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-colors ${
          overlayEnabled
            ? "bg-[#113F67] text-white hover:bg-[#0d2f4f]"
            : "bg-white text-[#113F67] border border-gray-200 hover:bg-gray-50"
        }`}
      >
        {overlayEnabled ? <X className="h-5 w-5" /> : <StickyNote className="h-5 w-5" />}
      </button>

      {overlayEnabled &&
        createPortal(
          <div className="pointer-events-none" aria-hidden={false}>
            {items.map(({ feedback, position }) => (
              <div
                key={feedback.id}
                role="button"
                tabIndex={0}
                onClick={() => dispatch(openDetailModal(feedback))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") dispatch(openDetailModal(feedback));
                }}
                className={`pointer-events-auto absolute z-50 w-56 max-w-[70vw] cursor-pointer rounded-md border-l-4 p-2.5 text-left shadow-md opacity-50 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none ${severityNoteClass[feedback.severity]} ${
                  position.approximate ? "border-dashed" : ""
                }`}
                style={{ top: position.top, left: position.left }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${severityDotClass[feedback.severity]}`} />
                  <span className="text-[11px] font-semibold text-gray-700 truncate">
                    {feedback.user?.name ?? labels.systemUser}
                  </span>
                  <span className="text-[10px] text-gray-500 ml-auto shrink-0">
                    {moment(feedback.created_at).fromNow()}
                  </span>
                </div>
                <p className="text-xs text-gray-800 leading-snug line-clamp-3 break-words">
                  {feedback.message}
                </p>
              </div>
            ))}
          </div>,
          document.body
        )}

      {overlayEnabled && !loading && items.length === 0 && (
        <div className="fixed bottom-24 right-6 z-[60] rounded-md bg-gray-900/80 text-white text-xs px-3 py-1.5 shadow-lg pointer-events-none">
          {labels.overlayNoRecords}
        </div>
      )}

      <DevFeedbackDetailModal />
    </>
  );
};

export default FeedbackOverlayLayer;
